import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import ArticleCard from '@/components/feature/ArticleCard';
import FAQ from '@/components/feature/FAQ';
import { categories, getFeaturedArticles, getLatestArticles, faqs } from '@/mocks/articles';

export default function Home() {
  const featuredArticles = getFeaturedArticles();
  const latestArticles = getLatestArticles(6);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '明智育儿指南',
    url: 'https://aitdev.cn',
    description: '围绕传统管教方式，深度解析心理学依据、适用争议、替代方案及专家建议，帮助家长科学理解打屁股对儿童身心发展的影响。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://aitdev.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEO
        title="打屁股——家庭教育与儿童行为管理完全指南 | 明智育儿指南"
        description="围绕打屁股这一传统管教方式，深度解析心理学依据、适用争议、替代方案及专家建议。帮助家长科学理解打屁股对儿童身心的影响，掌握正面管教方法，做出更明智的教育选择。"
        keywords="打屁股,打屁股对孩子的影响,打屁股教育争议,儿童行为管理方法,体罚与正面管教,替代打屁股的教育方式,家长管教技巧"
        schemaJson={[homeSchema]}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20abstract%20illustration%20of%20gentle%20parenting%2C%20soft%20flowing%20organic%20shapes%20in%20cream%20beige%20and%20terracotta%20tones%2C%20minimalist%20editorial%20background%2C%20calm%20and%20therapeutic%20atmosphere%2C%20subtle%20texture%2C%20clean%20composition%20with%20ample%20negative%20space&width=1600&height=900&seq=hero-parenting-2026&orientation=landscape"
              alt="家庭教育与儿童行为管理"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              科学理解管教，智慧陪伴成长
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed mb-8">
              深度解析打屁股对儿童身心发展的影响，探索正面管教的科学方法，帮助每一位家长做出更明智的教育选择。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/tidai-fangan"
                className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                探索替代方案
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/category/xinlixue-jiexi"
                className="inline-flex items-center px-6 py-3 rounded-full bg-white/20 text-white border border-white/30 font-medium text-sm whitespace-nowrap hover:bg-white/30 transition-colors cursor-pointer"
              >
                了解心理学依据
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                探索内容分类
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                从心理学解析到实用管教技巧，为您提供全方位的育儿知识体系
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center text-center p-6 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                    {cat.description}
                  </p>
                  <span className="text-xs text-foreground-400 mt-3 whitespace-nowrap">
                    {cat.articleCount} 篇文章
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                精选文章
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                由儿童心理学专家精心撰写，深入解析家长最关心的管教话题
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">
                关于打屁股和儿童管教的常见疑问，专家为您一一解答
              </p>
            </div>
            <FAQ items={faqs} />
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新文章
                </h2>
                <p className="text-sm text-foreground-600">
                  持续更新的育儿知识与管教方法
                </p>
              </div>
              <Link
                to="/category/jiaoyu-zhengyi"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-24 bg-background-50 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <img
              src="https://readdy.ai/api/search-image?query=A%20soft%20abstract%20pattern%20with%20gentle%20organic%20shapes%2C%20cream%20and%20beige%20tones%2C%20very%20subtle%20and%20minimalist%2C%20clean%20texture%20background&width=1600&height=600&seq=cta-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              开始您的科学育儿之旅
            </h2>
            <p className="text-sm text-foreground-600 mb-8 max-w-lg mx-auto leading-relaxed">
              每一位家长都值得拥有科学的育儿支持。浏览我们的文章，或联系我们的专家团队获取一对一咨询。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="inline-flex items-center px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                了解更多
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <a
                href="mailto:contact@wise-parenting-guide.cn"
                className="inline-flex items-center px-6 py-3 rounded-full border border-background-300/60 text-foreground-700 font-medium text-sm whitespace-nowrap hover:border-primary-500 hover:text-primary-500 transition-colors cursor-pointer"
              >
                联系我们
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}